import { useCallback, useMemo, useEffect, useState } from "react";
import { View, Text, Image, Pressable, StyleSheet, Linking, Modal } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  faPenToSquare,
  faEnvelope,
  faCommentSms,
  faHouse,
  faSchool,
  faXmark,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import { faShieldHalved, faBallotCheck, faFireFlame } from "@fortawesome/pro-solid-svg-icons";
import { faCalendarCirclePlus, faCalendar } from "@fortawesome/pro-solid-svg-icons";
import {
  FontAwesomeIcon,
  tabIconSize,
  homeIconSizes,
  logAnalyticsEvent,
  logError,
  os,
  screenWidth,
} from "../../platform";
import AppStore from "../../storage/appStore";
import SEOHeaderTags from "../SEOHeaderTags";
import PropertyMap from "./PropertyMap";
import NearbyPlaces from "./NearbyPlaces";
import FabButton from "../../sub/components/FabButton";
import { completeDayOfWeekString } from "../../utils/stringUtils";
import { STRINGS } from "../../constants/strings";
import { COLORS } from "../../theme/colors";
import { addToCalendar } from "../../utils/calendarAlerts";
import { getPropertyInfo, getNearbyPlaces } from "../../utils/api/propertyApi";
import { subDays, parseISO, format, differenceInCalendarDays } from "date-fns";

const treeGreen = require("../../assets/tree_green.png");

/**
 * Displays property information including address, garbage/recycling pickup schedules,
 * nearby places (fire stations, police, libraries, schools, parks), and notification options.
 * Provides calendar integration for pickup reminders and modal views for nearby facilities.
 * @returns {JSX.Element} Rendered property information component
 */
const PropertyInfo = () => {
  const propertyInfo = AppStore((state) => state.propertyInfo);
  const garbageInfo = AppStore((state) => state.garbageInfo);
  const propertyInfoMapBoundaries = AppStore((state) => state.propertyInfoMapBoundaries);
  const votingInfo = AppStore((state) => state.votingInfo);
  const nearbySchools = AppStore((state) => state.nearbySchools);
  const nearbyPolice = AppStore((state) => state.nearbyPolice);
  const nearbyFire = AppStore((state) => state.nearbyFire);
  const nearbyParks = AppStore((state) => state.nearbyParks);
  const nearbyLibraries = AppStore((state) => state.nearbyLibraries);
  const setShowAlert = AppStore((state) => state.setShowAlert);
  const setGarbageInfo = AppStore((state) => state.setGarbageInfo);
  const setVotingInfo = AppStore((state) => state.setVotingInfo);
  const setNearbySchools = AppStore((state) => state.setNearbySchools);
  const setNearbyPolice = AppStore((state) => state.setNearbyPolice);
  const setNearbyFire = AppStore((state) => state.setNearbyFire);
  const setNearbyParks = AppStore((state) => state.setNearbyParks);
  const setNearbyLibraries = AppStore((state) => state.setNearbyLibraries);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const setNotificationSubscription = AppStore((state) => state.setNotificationSubscription);
  const setLoading = AppStore((state) => state.setLoading);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlaceType, setSelectedPlaceType] = useState(null);
  const [selectedPlaceTypeData, setSelectedPlaceTypeData] = useState(null);

  const navigation = useNavigation();

  /**
   * Maps place types to their corresponding FontAwesome icons.
   * Used for displaying icons in the nearby places modal.
   * @type {Object.<string, import('@fortawesome/fontawesome-svg-core').IconDefinition>}
   * @property {IconDefinition} fire - Fire flame icon for fire stations
   * @property {IconDefinition} police - Police officer icon for police stations
   * @property {IconDefinition} library - Books icon for libraries
   * @property {IconDefinition} schools - School icon for schools
   * @property {IconDefinition} park - Tree icon for parks
   * @property {IconDefinition} voting - Ballot check icon for voting locations
   */
  const placeTypeIcons = useMemo(
    () => ({
      fire: { icon: faFireFlame, color: COLORS.red },
      police: { icon: faShieldHalved, color: COLORS.vbNavy },
      libraries: { icon: faBookOpen, color: COLORS.vbCyan },
      schools: { icon: faSchool, color: COLORS.vbOrange },
      parks: { image: treeGreen, style: styles.image },
      voting: { icon: faBallotCheck, color: COLORS.vbNavy },
    }),
    []
  );

  /**
   * Updates property information when garbage pickup date becomes stale.
   * Fetches fresh data if the next garbage date has passed.
   * @async
   * @returns {Promise<void>}
   */
  useEffect(() => {
    const updateStalePropertyInfo = async () => {
      if (!propertyInfo?.gpin || !propertyInfo?.garbage?.garbageDay) return;

      const nextGarbageDate = propertyInfo.garbage?.nextGarbageDate?.iso;
      const today = format(new Date(), "yyyy-MM-dd");
      const difference = differenceInCalendarDays(parseISO(nextGarbageDate), parseISO(today));

      if (difference >= 0) return;

      try {
        setLoading(true);
        const response = await getPropertyInfo(propertyInfo.address);
        setGarbageInfo(response.propertyInfo.garbage);
      } catch (error) {
        setErrorMessage(STRINGS.failedToLoadData);
      } finally {
        setLoading(false);
      }
    };

    updateStalePropertyInfo();
  }, [setLoading, setErrorMessage, setGarbageInfo, propertyInfo]);

  /**
   * Memoized accessibility states for various interactive elements.
   * @type {Object}
   */
  const accessibilityStates = useMemo(
    () => ({
      propertyInfo: { accessible: true, accessibilityRole: "button", accessibilityLabel: "Property information" },
      holidaySchedule: { accessible: true, accessibilityRole: "button", accessibilityLabel: "Holiday schedule" },
      editAddress: { accessible: true, accessibilityRole: "button", accessibilityLabel: "Edit address" },
      emailNotifications: { accessible: true, accessibilityRole: "button", accessibilityLabel: "Email notifications" },
      textNotifications: { accessible: true, accessibilityRole: "button", accessibilityLabel: "Text notifications" },
      addressText: { accessible: true, accessibilityRole: "text" },
    }),
    []
  );

  /**
   * Extracts the day of week from the recycle week string.
   * @type {string}
   */
  const recycleDay = useMemo(() => {
    return garbageInfo?.recycleWeek?.split(" ")?.[0];
  }, [garbageInfo?.recycleWeek]);

  /**
   * Adds a pickup event to the calendar.
   * @async
   * @param {string} type - Type of pickup ('garbage' or 'recycling')
   * @param {string} address - Property address for the event
   * @returns {Promise<void>}
   */
  const addPickupToCalendar = useCallback(
    async (type, address) => {
      const garbage = garbageInfo;

      if (!garbage) {
        setErrorMessage(STRINGS.noPropertyInfoAvailable);
        return;
      }

      if (!["garbage", "recycling"].includes(type)) {
        setErrorMessage(STRINGS.invalidPickupType);
        return;
      }

      try {
        const isGarbage = type === "garbage";
        const dateSource = isGarbage ? garbage?.nextGarbageDate : garbage?.nextRecyclingDate;

        if (!dateSource?.iso) {
          setErrorMessage(`${isGarbage ? "Garbage" : "Recycling"} pickup date not available`);
          return;
        }

        const date = new Date(dateSource.iso);
        if (isNaN(date.getTime())) {
          setErrorMessage("Invalid pickup date");
          return;
        }

        const title = isGarbage
          ? STRINGS.garbageNotifications.garbagePickupTitle
          : STRINGS.garbageNotifications.recyclePickupTitle;
        const description = isGarbage
          ? STRINGS.garbageNotifications.garbagePickupDescription
          : STRINGS.garbageNotifications.recyclePickupDescription;
        const eventType = isGarbage ? "garbage_pickup" : "recycle_pickup";

        const pickupDate = parseISO(dateSource.iso);
        const reminderDate = subDays(pickupDate, 1);
        const reminderDateStr = format(reminderDate, "yyyy-MM-dd");

        await addToCalendar({
          name: `${title} - ${address}`,
          start: reminderDateStr,
          end: reminderDateStr,
          startTime: 1700,
          endTime: 1730,
          address: address,
          description,
          repeatRule: { frequency: "WEEKLY", interval: isGarbage ? 1 : 2 },
        });

        logAnalyticsEvent(eventType, { name: `${dateSource.iso}-${address}` });
      } catch (error) {
        logError("calendar_event_add_failed", {
          message: error.message,
          stack: error.stack,
          type,
          address,
          date: garbage?.[type === "garbage" ? "nextGarbageDate" : "nextRecyclingDate"]?.iso,
        });
        setErrorMessage(STRINGS.failedToAddEventToCalendar);
      }
    },
    [setErrorMessage, garbageInfo]
  );

  /**
   * Opens the property information page in an external browser.
   * @async
   * @returns {Promise<void>}
   */
  const handlePropertyInfoPress = useCallback(async () => {
    const url = `${process.env.REACT_APP_PROPERTY_INFO_LINK}#/property/${propertyInfo.gpin}`;
    try {
      if (os === "web") {
        await Linking.openURL(url);
      } else {
        navigation.navigate("WebViewScreen", {
          url,
          title: STRINGS.propertyInfo,
        });
      }
    } catch (error) {
      logError("property_info_link_failed", {
        message: error.message,
        stack: error.stack,
        gpin: propertyInfo.gpin,
        url,
      });
      setErrorMessage(STRINGS.failedToOpenLink);
    }
  }, [navigation, propertyInfo.gpin, setErrorMessage]);

  /**
   * Opens the holiday schedule page in an external browser.
   * @async
   * @returns {Promise<void>}
   */
  const handleHolidaySchedulePress = useCallback(async () => {
    const url = STRINGS.holidayScheduleLink;
    try {
      if (os === "web") {
        await Linking.openURL(url);
      } else {
        navigation.navigate("WebViewScreen", {
          url,
          title: STRINGS.holidaySchedule,
        });
      }
    } catch (error) {
      logError("holiday_schedule_link_failed", {
        message: error.message,
        stack: error.stack,
        gpin: propertyInfo.gpin,
        url,
      });
      setErrorMessage(STRINGS.failedToOpenLink);
    }
  }, [navigation, propertyInfo.gpin, setErrorMessage]);

  /**
   * Shows the address edit alert dialog.
   * @returns {void}
   */
  const handleEditAddressPress = useCallback(() => {
    setShowAlert(true);
  }, [setShowAlert]);

  /**
   * Opens the email notification subscription dialog.
   * @returns {void}
   */
  const handleEmailNotificationPress = useCallback(() => {
    setNotificationSubscription({ show: true, type: "email" });
  }, [setNotificationSubscription]);

  /**
   * Opens the text notification subscription dialog.
   * @returns {void}
   */
  const handleTextNotificationPress = useCallback(() => {
    setNotificationSubscription({ show: true, type: "text" });
  }, [setNotificationSubscription]);

  /**
   * Adds garbage pickup event to the calendar.
   * @returns {void}
   */
  const handleGarbageCalendarPress = useCallback(() => {
    addPickupToCalendar("garbage", propertyInfo.address);
  }, [propertyInfo.address, addPickupToCalendar]);

  /**
   * Adds recycling pickup event to the calendar.
   * @returns {void}
   */
  const handleRecyclingCalendarPress = useCallback(() => {
    addPickupToCalendar("recycling", propertyInfo.address);
  }, [propertyInfo.address, addPickupToCalendar]);

  /**
   * Opens the nearby places modal for the specified place type.
   * @param {string} placeType - Type of place ('fire', 'police', 'libraries', 'schools', 'parks', 'voting')
   * @returns {void}
   */
  const handleNearbyPlacePress = useCallback(
    async (placeType, storeObject, storeObjectSetFn) => {
      setSelectedPlaceType(placeType);

      if (
        Object.keys(storeObject).length === 0 ||
        (placeType === "schools" && (!storeObject?.elementary || !storeObject?.middle || !storeObject?.high))
      ) {
        try {
          setLoading(true);
          const response = await getNearbyPlaces(placeType, propertyInfo);
          const data = response?.nearbyPlaces;
          storeObjectSetFn(data);
          setSelectedPlaceTypeData(data);
        } catch (error) {
          setErrorMessage(STRINGS.failedToLoadData);
        } finally {
          setLoading(false);
        }
      } else {
        setSelectedPlaceTypeData(storeObject);
      }

      setModalVisible(true);
    },
    [propertyInfo, setLoading, setErrorMessage]
  );

  /**
   * Closes the nearby places modal.
   * @returns {void}
   */
  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedPlaceType(null);
    setSelectedPlaceTypeData(null);
  }, []);

  /**
   * Accessibility label for garbage pickup button.
   * @type {string}
   */
  const garbageAccessibilityLabel = useMemo(
    () => `Garbage pickup on ${garbageInfo?.nextGarbageDate?.display} ${garbageInfo?.garbageDay}`,
    [garbageInfo?.nextGarbageDate?.display, garbageInfo?.garbageDay]
  );

  /**
   * Accessibility label for recycling pickup button.
   * @type {string}
   */
  const recyclingAccessibilityLabel = useMemo(
    () => `Recycling pickup on ${garbageInfo?.nextRecyclingDate?.display} ${recycleDay}`,
    [garbageInfo?.nextRecyclingDate?.display, recycleDay]
  );

  return (
    <View>
      <View style={[styles.nearbyPropertyButtonsContainer, styles.nearbyPropertyButtonsContainerLeft]}>
        <Pressable
          style={styles.nearbyPropertyButton}
          onPress={() => handleNearbyPlacePress("police", nearbyPolice, setNearbyPolice)}
          testID="nearby-police-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View nearby police stations"
        >
          <FontAwesomeIcon icon={faShieldHalved} size={tabIconSize} color={COLORS.vbNavy} />
        </Pressable>
        <Pressable
          style={styles.nearbyPropertyButton}
          onPress={() => handleNearbyPlacePress("fire", nearbyFire, setNearbyFire)}
          testID="nearby-fire-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View nearby fire stations"
        >
          <FontAwesomeIcon icon={faFireFlame} size={tabIconSize} color={COLORS.red} />
        </Pressable>
        <Pressable
          style={[styles.nearbyPropertyButton, styles.imageButton]}
          onPress={() => handleNearbyPlacePress("parks", nearbyParks, setNearbyParks)}
          testID="nearby-parks-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View nearby parks"
        >
          <Image source={treeGreen} style={styles.image} resizeMode="contain" />
        </Pressable>
      </View>
      {propertyInfo.latLng?.lat && propertyInfo.latLng?.lng && (
        <PropertyMap
          lat={propertyInfo.latLng?.lat}
          lng={propertyInfo.latLng?.lng}
          boundaries={propertyInfoMapBoundaries}
        />
      )}
      <View style={[styles.nearbyPropertyButtonsContainer, styles.nearbyPropertyButtonsContainerRight]}>
        <Pressable
          style={styles.nearbyPropertyButton}
          onPress={() => handleNearbyPlacePress("schools", nearbySchools, setNearbySchools)}
          testID="nearby-schools-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View nearby schools"
        >
          <FontAwesomeIcon icon={faSchool} size={tabIconSize} color={COLORS.vbOrange} />
        </Pressable>
        <Pressable
          style={styles.nearbyPropertyButton}
          onPress={() => handleNearbyPlacePress("libraries", nearbyLibraries, setNearbyLibraries)}
          testID="nearby-libraries-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View nearby libraries"
        >
          <FontAwesomeIcon icon={faBookOpen} size={tabIconSize} color={COLORS.vbCyan} />
        </Pressable>
        <Pressable
          style={styles.nearbyPropertyButton}
          onPress={() => handleNearbyPlacePress("voting", votingInfo, setVotingInfo)}
          testID="nearby-voting-button"
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="View voting information"
        >
          <FontAwesomeIcon icon={faBallotCheck} size={tabIconSize} color={COLORS.vbNavy} />
        </Pressable>
      </View>
      <FabButton
        testID="change-address-button"
        customStyle={styles.editAddressButton}
        icon={faPenToSquare}
        iconColor={COLORS.text}
        onPress={handleEditAddressPress}
        accessibilityLabel="Edit address"
      />
      <View style={styles.addressContainer}>
        <View style={styles.addressPlaceholder} />
        <View style={styles.addressTextContainer}>
          <Text style={styles.addressHeader} {...accessibilityStates.addressText} maxFontSizeMultiplier={1.2}>
            {propertyInfo.address}
          </Text>
          <Text style={styles.addressSubHeader} {...accessibilityStates.addressText} maxFontSizeMultiplier={1.2}>
            {propertyInfo.city}, {propertyInfo.state} {propertyInfo.zip}
          </Text>
        </View>
        <Pressable
          onPress={handlePropertyInfoPress}
          style={[styles.notificationButton, styles.propertyInfoButton]}
          {...accessibilityStates.propertyInfo}
        >
          <FontAwesomeIcon icon={faHouse} size={tabIconSize} color={COLORS.textLight} />
          <Text style={styles.notificationButtonText} maxFontSizeMultiplier={1.2}>
            {STRINGS.propertyInfo}
          </Text>
        </Pressable>
      </View>
      {propertyInfo?.garbage?.garbageDay && (
        <View style={styles.notificationsContainer}>
          <SEOHeaderTags
            style={styles.garbagePickupHeader}
            text={STRINGS.garbageNotifications.garbagePickupAlerts}
            tag="h2"
          />
          <Text {...accessibilityStates.addressText} style={styles.garbagePickupSubheader} maxFontSizeMultiplier={1.2}>
            {STRINGS.garbageNotifications.garbagePickupSubheader}
          </Text>

          <View style={styles.notificationsButtonContainer}>
            <Pressable
              style={styles.notificationButton}
              onPress={handleEmailNotificationPress}
              {...accessibilityStates.emailNotifications}
            >
              <FontAwesomeIcon icon={faEnvelope} size={tabIconSize} color={COLORS.textLight} />
              <Text style={styles.notificationButtonText} maxFontSizeMultiplier={1.2}>
                {STRINGS.email}
              </Text>
            </Pressable>
            <Pressable
              style={styles.notificationButton}
              onPress={handleTextNotificationPress}
              {...accessibilityStates.textNotifications}
            >
              <FontAwesomeIcon icon={faCommentSms} size={tabIconSize} color={COLORS.textLight} />
              <Text style={styles.notificationButtonText} maxFontSizeMultiplier={1.2}>
                {STRINGS.text}
              </Text>
            </Pressable>
          </View>

          <Pressable
            style={styles.calendarAlertButton}
            onPress={handleGarbageCalendarPress}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={garbageAccessibilityLabel}
          >
            <View style={styles.calendarAlertContentContainer}>
              <View style={styles.calendarAlertTextContainer}>
                <Text style={styles.calendarAlertButtonHeader} maxFontSizeMultiplier={1.2}>
                  {garbageInfo?.nextGarbageDate?.display}
                </Text>
                <Text style={styles.calendarAlertButtonSubheader} maxFontSizeMultiplier={1.2}>
                  {completeDayOfWeekString(garbageInfo?.garbageDay)} • {STRINGS.garbageNotifications.garbagePickup}
                </Text>
              </View>
              <FontAwesomeIcon icon={faCalendarCirclePlus} size={homeIconSizes.garbage} color={COLORS.red} />
            </View>
          </Pressable>

          <Pressable
            style={styles.calendarAlertButton}
            onPress={handleRecyclingCalendarPress}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={recyclingAccessibilityLabel}
          >
            <View style={styles.calendarAlertContentContainer}>
              <View style={styles.calendarAlertTextContainer}>
                <Text style={styles.calendarAlertButtonHeader} maxFontSizeMultiplier={1.2}>
                  {garbageInfo?.nextRecyclingDate?.display}
                </Text>
                <Text style={styles.calendarAlertButtonSubheader} maxFontSizeMultiplier={1.2}>
                  {completeDayOfWeekString(recycleDay)} • {STRINGS.garbageNotifications.recyclePickup}
                </Text>
              </View>
              <FontAwesomeIcon icon={faCalendarCirclePlus} size={homeIconSizes.garbage} color={COLORS.red} />
            </View>
          </Pressable>

          <Pressable
            onPress={handleHolidaySchedulePress}
            style={[styles.notificationButton, styles.holidayScheduleButton]}
            {...accessibilityStates.holidaySchedule}
          >
            <FontAwesomeIcon icon={faCalendar} size={tabIconSize} color={COLORS.textLight} />
            <Text style={styles.notificationButtonText} maxFontSizeMultiplier={1.2}>
              {STRINGS.holidaySchedule}
            </Text>
          </Pressable>
        </View>
      )}

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={handleCloseModal}
        testID="nearby-places-modal"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContentWrapper}>
            <View style={styles.modalContent}>
              <Pressable
                style={styles.modalCloseButton}
                onPress={handleCloseModal}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="Close nearby places"
              >
                <FontAwesomeIcon icon={faXmark} color={COLORS.tertiary} size={tabIconSize} />
              </Pressable>
              {selectedPlaceType && selectedPlaceTypeData && (
                <NearbyPlaces
                  title={STRINGS.nearbyPlaces?.[selectedPlaceType] || STRINGS.nearbyPlaces?.default || "Nearby Places"}
                  type={selectedPlaceType}
                  nearbyPlaces={selectedPlaceTypeData}
                  district={propertyInfo.votingDistrict}
                  icon={placeTypeIcons[selectedPlaceType]}
                  onNavigate={handleCloseModal}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

/**
 * StyleSheet for PropertyInfo component.
 * Includes styles for address display, notification buttons, calendar alerts,
 * nearby place buttons, and modal overlay.
 * @type {Object}
 */
const styles = StyleSheet.create({
  addressPlaceholder: {
    flex: 1,
  },
  addressContainer: {
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 10,
    marginBottom: 0,
  },
  addressTextContainer: {
    width: "90%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 2,
  },
  addressHeader: {
    fontSize: 24,
    fontWeight: "bold",
  },
  addressSubHeader: {
    fontSize: 16,
    color: COLORS.textSecondary,
  },
  propertyInfoButton: {
    width: 180,
  },
  holidayScheduleButton: {
    width: 220,
    alignSelf: "center",
  },
  nearbyPropertyButtonsContainer: {
    flexDirection: "column",
    position: "absolute",
    height: 200,
    justifyContent: "flex-end",
    gap: 8,
  },
  nearbyPropertyButtonsContainerLeft: {
    left: screenWidth >= 750 ? 200 : undefined,
    right: screenWidth < 750 ? screenWidth / 2 + 110 : undefined,
  },
  nearbyPropertyButtonsContainerRight: {
    right: screenWidth >= 750 ? 200 : undefined,
    left: screenWidth < 750 ? screenWidth / 2 + 110 : undefined,
  },
  nearbyPropertyButton: {
    padding: 5,
    alignContent: "center",
    justifyContent: "center",
    height: 36,
    width: 36,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: COLORS.border,
  },
  imageButton: {
    padding: 0,
    paddingTop: 4,
  },
  image: {
    width: 35,
    height: 35,
  },
  editAddressButton: {
    position: "absolute",
    top: 3,
    right: 3,
    width: os === "web" ? 35 : 40,
    height: os === "web" ? 35 : 40,
    borderRadius: 9999,
    backgroundColor: COLORS.textLight,
    paddingRight: 8,
    paddingTop: 8,
  },
  notificationsContainer: {
    flexDirection: "column",
  },
  garbagePickupHeader: {
    fontSize: 21,
    fontWeight: "bold",
    color: COLORS.textSecondary,
    marginTop: 14,
    marginBottom: 5,
    textAlign: "center",
  },
  garbagePickupSubheader: {
    fontSize: 16,
    color: COLORS.textSecondary,
    width: "75%",
    textAlign: "center",
    borderWidth: 0,
    margin: "auto",
    marginTop: 1,
  },
  notificationsButtonContainer: {
    flexDirection: "row",
    gap: 20,
    margin: "auto",
    marginTop: 6,
  },
  notificationButton: {
    flexDirection: "row",
    gap: 5,
    borderRadius: 25,
    marginVertical: 10,
    padding: 10,
    backgroundColor: COLORS.red,
    alignItems: "center",
    justifyContent: "center",
    width: 130,
  },
  notificationButtonText: {
    fontSize: 16,
    color: COLORS.textLight,
  },
  calendarAlertContentContainer: {
    flexDirection: "row",
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
  },
  calendarAlertButton: {
    flexDirection: "row",
    gap: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: "center",
    width: "100%",
    padding: 15,
    marginVertical: 3,
  },
  calendarAlertTextContainer: {
    flexDirection: "column",
  },
  calendarAlertButtonHeader: {
    fontSize: 18,
    fontWeight: "bold",
    paddingBottom: 5,
  },
  calendarAlertButtonSubheader: {
    fontSize: 16,
    color: COLORS.textSecondary,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.secondaryTranslucent,
  },
  modalContentWrapper: {
    width: screenWidth > 750 ? 700 : "90%",
    maxHeight: "80%",
    backgroundColor: COLORS.background || "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalContent: {
    padding: 20,
    paddingTop: 30,
    width: "100%",
    ...(os === "web" ? { height: "100%" } : {}),
  },
  modalCloseButton: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 10,
  },
});

export default PropertyInfo;
